import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Clock, Briefcase, ChevronRight, ChevronDown,
  Loader2, FileText, AlertCircle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface CaseDetail {
  case_id: string;
  case_title: string;
  hourly_rate: number;
  total_seconds: number;
  total_hours: number;
  total_amount: number;
  entries_count: number;
  date_range: {
    start: string | null;
    end: string | null;
  };
}

interface InvoiceSuggestion {
  client_id: string;
  client_name: string;
  client_email?: string;
  cases: CaseDetail[];
  case_count: number;
  total_hours: number;
  total_amount: number;
}

interface InvoiceSuggestionsProps {
  onInvoiceCreated: () => void;
}

const InvoiceSuggestions: React.FC<InvoiceSuggestionsProps> = ({ onInvoiceCreated }) => {
  const { t, i18n } = useTranslation('invoicing');
  const currentLanguage = i18n.language?.split('-')[0] || 'no';
  
  const [suggestions, setSuggestions] = useState<InvoiceSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [creatingInvoice, setCreatingInvoice] = useState<string | null>(null);
  const [totalUnbilledHours, setTotalUnbilledHours] = useState(0);
  const [totalUnbilledAmount, setTotalUnbilledAmount] = useState(0);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/invoices/suggestions/unbilled`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      setSuggestions(response.data.suggestions || []);
      setTotalUnbilledHours(response.data.total_unbilled_hours || 0);
      setTotalUnbilledAmount(response.data.total_unbilled_amount || 0);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      toast.error(t('suggestions.couldNotFetch'));
    } finally {
      setLoading(false);
    }
  };

  const createInvoiceFromSuggestion = async (suggestion: InvoiceSuggestion) => {
    try {
      setCreatingInvoice(suggestion.client_id);
      
      const caseIds = suggestion.cases.map(c => c.case_id);
      
      const response = await axios.post(`${API}/invoices/from-suggestion`, null, {
        params: {
          client_id: suggestion.client_id,
          case_ids: caseIds,
          due_days: 14
        },
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      
      toast.success(t('suggestions.invoiceCreated', { number: response.data.invoice_number }));
      
      // Refresh suggestions and notify parent
      await fetchSuggestions();
      onInvoiceCreated();
      
    } catch (error: any) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.detail || t('suggestions.couldNotCreateInvoice'));
    } finally {
      setCreatingInvoice(null);
    }
  };

  const formatCurrency = (value: number) => {
    if (currentLanguage === 'en') {
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return `${value.toLocaleString('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} kr`;
  };

  const formatHours = (hours: number) => {
    return `${hours.toFixed(1)}${currentLanguage === 'en' ? 'h' : 't'}`;
  };

  const formatDateRange = (start: string | null, end: string | null) => {
    if (!start) return '';
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const locale = currentLanguage === 'en' ? 'en-US' : 'nb-NO';
    if (start === end || !end) {
      return new Date(start).toLocaleDateString(locale, opts);
    }
    return `${new Date(start).toLocaleDateString(locale, opts)} - ${new Date(end).toLocaleDateString(locale, opts)}`;
  };

  const getCaseLabel = (count: number) => {
    if (count === 1) {
      return t('suggestions.case');
    }
    return t('suggestions.cases');
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-100">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-100">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <h3 className="text-base font-medium text-gray-700">{t('suggestions.noUnbilledTime')}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {t('suggestions.noUnbilledTimeDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-100 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-violet-100 bg-white/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">{t('suggestions.title')}</h3>
              <p className="text-sm text-gray-500">
                {t('suggestions.clientsWithUnbilledTime', { count: suggestions.length })}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-violet-700">{formatCurrency(totalUnbilledAmount)}</div>
            <div className="text-xs text-gray-500">{formatHours(totalUnbilledHours)} {t('suggestions.unbilledSuffix')}</div>
          </div>
        </div>
      </div>

      {/* Suggestions list */}
      <div className="divide-y divide-violet-100">
        {suggestions.map((suggestion) => (
          <div key={suggestion.client_id} className="bg-white/30">
            {/* Client row */}
            <div 
              className="px-6 py-4 hover:bg-white/50 transition-colors cursor-pointer"
              onClick={() => setExpandedClient(
                expandedClient === suggestion.client_id ? null : suggestion.client_id
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button className="text-gray-400 hover:text-gray-600">
                    {expandedClient === suggestion.client_id ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  <div>
                    <h4 className="font-medium text-gray-900">{suggestion.client_name}</h4>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-3.5 h-3.5" />
                        {suggestion.case_count} {getCaseLabel(suggestion.case_count)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatHours(suggestion.total_hours)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(suggestion.total_amount)}
                    </div>
                    <div className="text-xs text-gray-500">{t('suggestions.exVat')}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      createInvoiceFromSuggestion(suggestion);
                    }}
                    disabled={creatingInvoice === suggestion.client_id}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {creatingInvoice === suggestion.client_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                    {t('suggestions.createInvoice')}
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded case details */}
            <AnimatePresence>
              {expandedClient === suggestion.client_id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-4 pl-14">
                    <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-50">
                      {suggestion.cases.map((caseDetail) => (
                        <div key={caseDetail.case_id} className="px-4 py-3 flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-800 text-sm">
                              {caseDetail.case_title}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {formatHours(caseDetail.total_hours)} × {formatCurrency(caseDetail.hourly_rate)}/{currentLanguage === 'en' ? 'h' : 't'}
                              {caseDetail.date_range.start && (
                                <span className="ml-2">
                                  • {formatDateRange(caseDetail.date_range.start, caseDetail.date_range.end)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(caseDetail.total_amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvoiceSuggestions;
